import { SUPER_ADMIN_GRANT_ONLY_PERMISSIONS, USER_ROLES } from '@hygge/shared';
import { matchPermission } from '@/lib/permissions';

/**
 * Module-row logic of the Permission Matrix, kept out of the component for the
 * node vitest. Only super_admin may grant or remove the codes in
 * `SUPER_ADMIN_GRANT_ONLY_PERMISSIONS` (`@hygge/shared`) — the
 * server refuses such a save from anyone else — so for everyone else a module
 * row neither touches them nor counts them. [HW-1061] [HW-1062]
 */

type MatrixPermission = { id: string; code: string };

/** A code only super_admin may grant: one of the list, or a row that covers one (`settings.*`, `auth.*`, `*`). */
export function isSuperAdminOnlyCode(code: string): boolean {
  return SUPER_ADMIN_GRANT_ONLY_PERMISSIONS.some((restricted) => matchPermission(code, restricted));
}

/**
 * Whether this user may change a role's column. Editing follows
 * `settings.roles.manage`, not a role name; the super_admin role itself is
 * changed by super_admin only — the server refuses anyone else. [HW-1062]
 */
export function canEditRoleColumn(
  roleName: string,
  user: { isSuperAdmin: boolean; canManageRoles: boolean }
): boolean {
  if (user.isSuperAdmin) return true;
  return user.canManageRoles && roleName.trim() !== USER_ROLES.SUPER_ADMIN;
}

/** The permissions of a module this user may switch on and off. */
export function editableModulePermissions<T extends MatrixPermission>(
  modulePerms: T[],
  isSuperAdmin: boolean
): T[] {
  return isSuperAdmin ? modulePerms : modulePerms.filter((p) => !isSuperAdminOnlyCode(p.code));
}

/**
 * Ticks or clears a whole module in a role's set: clears it when every
 * editable permission is held, ticks it otherwise. A permission the user may
 * not change is left as it is.
 */
export function toggleModuleInSet(
  set: Set<string>,
  modulePerms: MatrixPermission[],
  isSuperAdmin: boolean
): void {
  const editable = editableModulePermissions(modulePerms, isSuperAdmin);
  if (editable.length === 0) return;
  const allHave = editable.every((p) => set.has(p.id));
  for (const p of editable) {
    if (allHave) set.delete(p.id);
    else set.add(p.id);
  }
}

/**
 * The module row's checkbox state, counting only what this user may change —
 * so a module whose only missing permission is one they cannot grant reads as
 * ticked, and clicking it clears rather than asks for a refused grant. A
 * module with nothing editable shows the state of what it holds.
 */
export function moduleCheckState(
  modulePerms: MatrixPermission[],
  has: (permId: string) => boolean,
  isSuperAdmin: boolean
): { checked: boolean | 'indeterminate'; editable: boolean } {
  const editable = editableModulePermissions(modulePerms, isSuperAdmin);
  const counted = editable.length > 0 ? editable : modulePerms;
  const count = counted.filter((p) => has(p.id)).length;
  const checked = count === 0 ? false : count === counted.length ? true : 'indeterminate';
  return { checked, editable: editable.length > 0 };
}

type DraftSets = Map<string, Set<string>>;

function sameSet(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
}

/**
 * The matrix draft: the server's sets, with this user's edits over the roles
 * they changed. Computed during render from the latest server state, so a
 * role this user did not touch always shows — and saves — what the server
 * holds now, a change someone else saved meanwhile included. `settled` names
 * the edits that no longer differ from the server (after a save, or an edit
 * undone) or whose role is gone, for the caller to drop. [HW-1062]
 */
export function mergeEdits(
  server: DraftSets,
  edits: DraftSets
): { draft: DraftSets; settled: string[] } {
  const draft: DraftSets = new Map();
  for (const [roleId, serverIds] of server) {
    const edit = edits.get(roleId);
    draft.set(roleId, edit && !sameSet(edit, serverIds) ? edit : serverIds);
  }
  const settled = [...edits.keys()].filter((roleId) => {
    const serverIds = server.get(roleId);
    return !serverIds || sameSet(edits.get(roleId)!, serverIds);
  });
  return { draft, settled };
}
